import React, { useMemo, useState } from 'react';
import { Alert, Badge, Button, Collapse, Modal } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { BiChevronDown, BiChevronUp, BiDownload } from 'react-icons/bi';
import { BsExclamationTriangle } from 'react-icons/bs';
import {
	buildValidationGroups,
	downloadValidationExcelReport,
} from '../../../../core/utilities/brsrValidationReport';
import './BRSRXbrlValidationModal.scss';

const MAX_EXAMPLE_LEN = 280;

function truncateExample(msg) {
	if (!msg || msg.length <= MAX_EXAMPLE_LEN) return msg;
	return `${msg.slice(0, MAX_EXAMPLE_LEN)}…`;
}

function BRSRXbrlValidationModal({
	t,
	show,
	validation,
	onHide,
	onDownloadExcel,
	onDownloadXbrlAnyway,
}) {
	const [expandedKeys, setExpandedKeys] = useState({});
	const [technicalOpen, setTechnicalOpen] = useState(false);

	const { groups, sectionGroups, totals } = useMemo(
		() => (validation ? buildValidationGroups(validation) : { groups: [], sectionGroups: [], totals: { errors: 0, warnings: 0, info: 0 } }),
		[validation]
	);

	const errorCount = validation?.error_count ?? totals.errors;
	const warningCount = validation?.warning_count ?? totals.warnings;
	const totalIssues = errorCount + warningCount + (validation?.info?.length ?? totals.info ?? 0);
	const isValid = validation?.is_valid === true;

	const toggleGroup = (key) => {
		setExpandedKeys((prev) => ({ ...prev, [key]: !prev[key] }));
	};

	const handleDownloadExcel = () => {
		if (onDownloadExcel) {
			onDownloadExcel();
		} else if (validation) {
			downloadValidationExcelReport(validation);
		}
	};

	return (
		<Modal
			show={show}
			onHide={onHide}
			size="lg"
			centered
			backdrop="static"
			keyboard
			className="brsr-xbrl-validation-modal"
			data-testid="brsr-xbrl-validation-modal"
			aria-labelledby="brsr-xbrl-validation-title"
		>
			<Modal.Header closeButton>
				<Modal.Title as="h5" id="brsr-xbrl-validation-title" className="d-flex align-items-center gap-2">
					{t('BRSRXbrlValidation.modalTitle.label')}
					{totalIssues > 0 && (
						<Badge bg="danger" pill data-testid="brsr-xbrl-validation-count">
							{totalIssues}
						</Badge>
					)}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<Alert variant="warning" className="brsr-xbrl-val-banner d-flex align-items-start gap-2 mb-3">
					<BsExclamationTriangle className="mt-1 flex-shrink-0" aria-hidden />
					<span>{t('BRSRXbrlValidation.banner.label')}</span>
				</Alert>

				<div className="brsr-xbrl-val-summary mb-3" data-testid="brsr-xbrl-validation-summary">
					<div className="brsr-xbrl-val-summary-item">
						<span className="brsr-xbrl-val-summary-label">{t('BRSRXbrlValidation.errors.label')}</span>
						<span className="brsr-xbrl-val-summary-value text-danger">{errorCount}</span>
					</div>
					<div className="brsr-xbrl-val-summary-item">
						<span className="brsr-xbrl-val-summary-label">{t('BRSRXbrlValidation.warnings.label')}</span>
						<span className="brsr-xbrl-val-summary-value text-warning">{warningCount}</span>
					</div>
					<div className="brsr-xbrl-val-summary-item">
						<span className="brsr-xbrl-val-summary-label">{t('BRSRXbrlValidation.status.label')}</span>
						<span
							className={`brsr-xbrl-val-status-chip ${isValid ? 'brsr-xbrl-val-status-chip--passed' : 'brsr-xbrl-val-status-chip--failed'}`}
							data-testid="brsr-xbrl-validation-status"
						>
							{isValid
								? t('BRSRXbrlValidation.statusPassed.label')
								: t('BRSRXbrlValidation.statusFailed.label')}
						</span>
					</div>
				</div>

				{groups.length === 0 ? (
					<p className="text-muted mb-0" data-testid="brsr-xbrl-validation-empty">
						{t('BRSRXbrlValidation.noIssuesDetail.label')}
					</p>
				) : (
					<>
						<h6 className="small text-muted text-uppercase mb-2">
							{t('BRSRXbrlValidation.groupedIssues.label')}
						</h6>
						<ul className="brsr-xbrl-val-group-list" aria-label={t('BRSRXbrlValidation.groupedIssues.label')}>
							{groups.map((g) => {
								const open = !!expandedKeys[g.key];
								return (
									<li key={g.key} className="brsr-xbrl-val-group-item">
										<button
											type="button"
											className="brsr-xbrl-val-group-header"
											onClick={() => toggleGroup(g.key)}
											aria-expanded={open}
											aria-controls={`brsr-val-examples-${g.key}`}
										>
											<div>
												<p className="brsr-xbrl-val-group-title">{g.issueTypeLabel}</p>
												<p className="brsr-xbrl-val-group-meta mb-0">
													{g.section} · {g.count}{' '}
													{g.count === 1
														? t('BRSRXbrlValidation.issueSingular.label')
														: t('BRSRXbrlValidation.issuePlural.label')}
												</p>
											</div>
											<span className="d-flex align-items-center gap-2">
												<span className="brsr-xbrl-val-count-badge">{g.count}</span>
												{open ? <BiChevronUp aria-hidden /> : <BiChevronDown aria-hidden />}
											</span>
										</button>
										<Collapse in={open}>
											<div id={`brsr-val-examples-${g.key}`} className="brsr-xbrl-val-examples">
												{g.examples.length > 0 ? (
													g.examples.map((ex, i) => (
														<pre key={i}>{truncateExample(ex)}</pre>
													))
												) : (
													<p className="mb-0 text-muted">{t('BRSRXbrlValidation.noExamples.label')}</p>
												)}
											</div>
										</Collapse>
									</li>
								);
							})}
						</ul>

						{sectionGroups.length > 0 && (
							<div className="brsr-xbrl-val-section-block">
								<div className="brsr-xbrl-val-section-heading">
									{t('BRSRXbrlValidation.bySection.label')}
								</div>
								<div className="brsr-xbrl-val-section-pills">
									{sectionGroups.slice(0, 12).map((sg) => (
										<span
											key={`${sg.section}-${sg.issueType}`}
											className="brsr-xbrl-val-section-pill"
										>
											{sg.section}: {sg.issueTypeLabel} ({sg.count})
										</span>
									))}
								</div>
							</div>
						)}
					</>
				)}

				{groups.some((g) => g.examples.length > 0) && (
					<div className="mt-3">
						<Button
							variant="link"
							className="brsr-xbrl-val-technical-toggle text-decoration-none p-0"
							onClick={() => setTechnicalOpen((o) => !o)}
							aria-expanded={technicalOpen}
							data-testid="brsr-xbrl-technical-toggle"
						>
							{t('BRSRXbrlValidation.technicalDetails.label')}
							{technicalOpen ? <BiChevronUp className="ms-1" /> : <BiChevronDown className="ms-1" />}
						</Button>
						<Collapse in={technicalOpen}>
							<div className="mt-2 small text-muted">
								{t('BRSRXbrlValidation.technicalDetailsHint.label')}
							</div>
						</Collapse>
					</div>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" onClick={onHide} data-testid="brsr-xbrl-validation-cancel">
					{t('Cancel.label')}
				</Button>
				<Button
					variant="outline-danger"
					onClick={onDownloadXbrlAnyway}
					data-testid="brsr-xbrl-download-anyway"
				>
					{t('BRSRXbrlValidation.downloadXbrlAnyway.label')}
				</Button>
				<Button
					variant="primary"
					onClick={handleDownloadExcel}
					data-testid="brsr-xbrl-download-excel"
				>
					<BiDownload className="me-1" aria-hidden />
					{t('BRSRXbrlValidation.downloadExcel.label')}
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

export default withTranslation()(BRSRXbrlValidationModal);
